import { useState } from "react";
import { LOCAL_STORAGE_KEYS } from "../lib/constants";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

interface UseFileUploadOptions {
  onSuccess?: (key: string) => void;
  onError?: (error: Error) => void;
}

export const useFileUpload = ({
  onSuccess,
  onError,
}: UseFileUploadOptions = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(
        `File size must be less than 5MB. Current size: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB`
      );
    }
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      validateFile(file);

      console.log("Starting file upload process for:", {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      });

      // Get pre-signed URL
      const presignedUrlEndpoint = `${
        import.meta.env.VITE_API_BASE_URL
      }/uploads/presigned-url`;
      console.log("Requesting presigned URL from:", presignedUrlEndpoint);

      const response = await fetch(presignedUrlEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to get pre-signed URL:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });
        throw new Error(errorData.error || "Failed to get upload URL");
      }

      const { signedUrl, key } = await response.json();
      console.log("Received presigned URL:", {
        key,
        urlLength: signedUrl.length,
        urlPreview: signedUrl.substring(0, 100) + "...",
      });

      // Upload file to R2
      console.log("Starting file upload to R2...");
      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error("Upload failed:", {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          error: errorText,
        });
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      console.log("File upload completed successfully");
      setProgress(100);

      // Store the file key in localStorage for persistence
      const fileData = {
        key,
        fileName: file.name,
        fileType: file.type,
        uploadedAt: new Date().toISOString(),
      };
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.UPLOADED_QUOTE,
        JSON.stringify(fileData)
      );

      onSuccess?.(key);
      return key;
    } catch (err) {
      console.error("Error in file upload process:", err);
      const error = err instanceof Error ? err : new Error("Upload failed");
      setError(error);
      onError?.(error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const getStoredFile = () => {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEYS.UPLOADED_QUOTE);
    return storedData ? JSON.parse(storedData) : null;
  };

  return {
    uploadFile,
    isUploading,
    progress,
    error,
    getStoredFile,
  };
};
